import Pattern from '@/assets/pattern.svg'
import Chat from '@/components/Chat'
import { Menu } from '@/components/Menu'
import { SearchParamsKeyNames, SearchParamsViewNames } from '@/routes'
import { storeActions } from '@/store/actions'
import {
  Beneficiary,
  ChatSession,
  Organization,
} from '@/store/beneficiary'
import { useStoreDispatch } from '@/store/hooks'
import { isEmptyString } from '@repo/shared/utils'
import { Avatar, AvatarFallback } from '@repo/ui/avatar'
import { DropdownMenuLabel } from '@repo/ui/dropdownMenu'
import { useT } from '@repo/ui/hooks'
import { Language } from '@repo/ui/lang'
import { cn, findLastChatSessionId } from '@repo/ui/utils'
import { ArrowUpRight, Megaphone, MessagesSquare } from 'lucide-react'
import { ComponentType, ReactNode, SVGProps, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

export type HomeProps = {
  beneficiary: Beneficiary
  organization: Organization
  chatSessions: ChatSession[]
}

function replaceVariableInText(str: string, organizationName: string): string {
  return str.replace(/\{\{\s*organization\.name\s*\}\}/g, organizationName)
}

export function Home({
  beneficiary,
  organization,
  chatSessions,
}: HomeProps) {
  const dispatch = useStoreDispatch()
  const { t, tc, i18n } = useT({ keyPrefix: 'beneficiary-app' })
  const [openChat, setOpenChat] = useState(false)
  const openLastChatSession = async () => {
    setOpenChat(true)
    const chatSessionId = findLastChatSessionId(chatSessions)
    if (chatSessionId) {
      await dispatch(storeActions.initChatSession({ chatSessionId }))
    }
  }

  useHandleQrCode(openLastChatSession)


  const getInformationNoticeText = (): string => {
    return replaceVariableInText(
      organization.texts?.beneficiary_app.information_notice[
        i18n.language as Language
      ] || t('informationNotice.description', { lng: i18n.language }),
      organization.name,
    )
  }
  const InformationNotice = ({ className }: { className: string }) => (
    <MenuItem
      icon={Megaphone}
      title={t('informationNotice.title')}
      className={className}
    >
      <p className="text-primary-foreground tall:my-4 my-2 px-6 text-lg font-light">
        {getInformationNoticeText()}
      </p>
      <div className="text-primary-foreground h-full flex-1 content-end items-end justify-end py-4 text-center text-xs font-light opacity-30">
        © {t('copyRight')}
      </div>
    </MenuItem>
  )

  const getWelcomeText = (): ReactNode => {
    const customText =
      organization.texts?.beneficiary_app.welcome?.[i18n.language as Language]
    const hasCustomText = customText && !isEmptyString(customText)

    return (
      <div className="tall:text-5xl whitespace-break-spaces text-pretty text-4xl">
        {hasCustomText
          ? replaceVariableInText(customText, organization.name)
          : t('welcome')}
      </div>
    )
  }

  return (
    <div className="flex flex-1 select-none flex-col md:inset-0 md:m-auto md:h-full md:max-w-[400px]">
      <div
        className="absolute inset-0 z-0 bg-cover bg-center opacity-15"
        style={{ backgroundImage: `url(${Pattern})` }}
      />
      <header className="tall:m-6 z-10 m-4 flex flex-shrink-0 items-center">
        <Menu
          trigger={
            <div className="flex w-fit gap-2">
              <Avatar className="h-12 w-12 rounded-full">
                <AvatarFallback className="text-primary rounded-lg">
                  {beneficiary.fullName
                    .split(' ')
                    .map((word) => word[0])
                    .join('')}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col items-start justify-center gap-px">
                <div className="text-primary text-sm font-medium leading-none">
                  {organization.name ?? 'CaseAI'}
                </div>
                <div className="text-primary-500 text-xl font-semibold leading-none">
                  {beneficiary.fullName}
                </div>
              </div>
            </div>
          }
        >
          <DropdownMenuLabel className="text-primary-500 flex items-center">
            {tc('settings')}
          </DropdownMenuLabel>
        </Menu>
        {organization.chatSettings?.imageUrl && (
          <div className="flex flex-1 items-center justify-end">
            <img
              src={organization.chatSettings?.imageUrl}
              alt="Organization Logo"
              className="max-h-12"
            />
          </div>
        )}
      </header>
      <main className="tall:mx-6 relative mx-4 flex flex-1 flex-col">
        <div className="tall:h-1/2 flex h-1/3 items-center">
          <h1 className="ml-1 flex w-full flex-shrink-0 flex-col font-medium">
            {getWelcomeText()}
          </h1>
        </div>
        <div className="tall:h-1/2 my-4 flex items-end">
          
            <MenuItem
              icon={MessagesSquare}
              title={t('chatSession.title')}
              subTitle={t('chatSession.description')}
              className="bg-primary-500"
              onClick={() => void openLastChatSession()}
            >
              <InformationNotice className="bg-primary-800" />
            </MenuItem>
          

          {/* Drawers */}
          <Chat modalHandler={{ open: openChat, setOpen: setOpenChat }} />
        </div>
      </main>
    </div>
  )
}

function MenuItem({
  children,
  className,
  icon: Icon,
  onClick,
  subTitle,
  title,
}: {
  children?: ReactNode
  className?: string
  icon: ComponentType<SVGProps<SVGSVGElement>>
  onClick?: () => void
  subTitle?: string
  title: string
}) {
  return (
    <div
      className={cn(
        'bg-primary-500 relative flex h-fit min-h-40 w-full flex-shrink-0 flex-col overflow-hidden rounded-3xl pt-2',
        className,
      )}
    >
      <div
        className="absolute inset-0 z-0 bg-cover bg-center opacity-15"
        style={{ backgroundImage: `url(${Pattern})` }}
      />
      <div
        className={cn(
          'relative z-10 mb-3 flex h-fit w-full items-start justify-between px-2',
          onClick && 'cursor-pointer',
        )}
        onClick={onClick}
      >
        <div className="flex-shrink-0 rounded-full bg-white/30 p-3 text-white">
          <Icon className="size-5" />
        </div>
        <div className={cn('flex-1 px-4', subTitle ? 'pt-1' : 'pt-3.5')}>
          <div className="text-lg font-medium leading-none text-white">
            {title}
          </div>
          {subTitle && (
            <div className="text-primary-foreground text-sm font-light">
              {subTitle}
            </div>
          )}
        </div>
        {onClick && (
          <div className="flex-shrink-0 rounded-full bg-white p-3">
            <ArrowUpRight className="size-5" />
          </div>
        )}
      </div>
      {children}
    </div>
  )
}

function useHandleQrCode(openLastChatSession: () => Promise<void>) {
  const [searchParams] = useSearchParams()
  const key = searchParams.get(SearchParamsKeyNames.Source)
  const isSourceQrCode = key === SearchParamsViewNames.QrCode
  useEffect(() => {
    if (isSourceQrCode) {
      void openLastChatSession()
    }
  }, [isSourceQrCode])
}
